/**
 * CONTRACT (Wave 0 · T0.3) — FROZEN.
 * Onboarding BFF route paths + request/response Zod schemas.
 *
 * Shared by the FE data layer (T2.4/T2.8) and the BFF routes (T2.5/T2.6/T2.7).
 * Downstream tasks import these; changes are serialized contract amendments.
 */
import { z } from 'zod';
import { OnboardingDefinitionV1Schema } from './onboarding-definition';
import { isRegisterPasswordValid, REGISTER_PASSWORD_HINT } from './password';

/* ----------------------------- Route paths ------------------------------ */

export const ONBOARDING_API_PREFIX = '/v1/onboarding';

export const ROUTES = {
  authRegister: '/v1/onboarding/auth/register',
  authLogin: '/v1/onboarding/auth/login',
  /** Platform-operator Cognito password login. Sets HttpOnly ID-token cookie. */
  authCognitoLogin: '/v1/onboarding/auth/cognito-login',
  /** Hydrate operator session from cookie / Bearer. */
  authSession: '/v1/onboarding/auth/session',
  authRefresh: '/v1/onboarding/auth/refresh',
  authLogout: '/v1/onboarding/auth/logout',
  /** Existence probe — lets the Account step detect an existing account early. */
  authCheckEmail: '/v1/onboarding/auth/check-email',
  usersMe: '/v1/onboarding/users/me',
  definition: '/v1/onboarding/definition',
  provision: '/v1/onboarding/provision',
  /** `:id` is the provisionId. */
  provisionStatus: '/v1/onboarding/provision/:id/status',
  /** `:id` is the provisionId. SSE stream of provisioning status frames. */
  provisionStream: '/v1/onboarding/provision/:id/stream',
} as const;

/* --------------------------- Error envelope ----------------------------- */

export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

/* ------------------------------ Auth DTOs -------------------------------- */

export const TokensSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.string(),
});

export const UserProfileSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  emailVerified: z.boolean(),
  status: z.string(),
  profile: z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    avatarUrl: z.string().optional(),
  }),
  marketingPreferences: z.object({
    emailOptIn: z.boolean(),
    smsOptIn: z.boolean(),
  }),
  hasPassword: z.boolean(),
  linkedAccounts: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const RegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z
    .string()
    .min(1)
    .refine(isRegisterPasswordValid, REGISTER_PASSWORD_HINT),
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  phone: z.string().max(50).optional(),
  marketingOptIn: z.boolean().optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const RegisterResponseSchema = z.object({
  user: UserProfileSchema,
  message: z.string(),
});
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** Login/refresh return tokens; the refresh token is set as an HttpOnly cookie. */
export const LoginResponseSchema = z.object({
  user: z.object({ displayName: z.string() }),
  tokens: TokensSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const RefreshResponseSchema = z.object({
  tokens: TokensSchema,
});
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

export const LogoutResponseSchema = z.object({ message: z.string() });
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

/** Operator session after Cognito ID-token verification. */
export const OperatorSessionSchema = z.object({
  email: z.string().optional(),
  groups: z.array(z.string()),
});
export type OperatorSession = z.infer<typeof OperatorSessionSchema>;

/**
 * Email-existence probe. Called by the Account step (register mode) BEFORE
 * advancing, so a returning merchant is routed to sign-in instead of spawning
 * an orphan tenant that would later 409 during provisioning. The BFF proxies to
 * the users service with the managed platform JWT and an empty tenant scope, so
 * `exists` reflects the same global-uniqueness check that produces the real 409.
 */
export const CheckEmailRequestSchema = z.object({
  email: z.string().email(),
});
export type CheckEmailRequest = z.infer<typeof CheckEmailRequestSchema>;

export const CheckEmailResponseSchema = z.object({
  exists: z.boolean(),
});
export type CheckEmailResponse = z.infer<typeof CheckEmailResponseSchema>;

/* --------------------------- Definition route --------------------------- */

export const DefinitionQuerySchema = z.object({
  version: z.string().default('latest'),
});
export const DefinitionResponseSchema = OnboardingDefinitionV1Schema;
export type DefinitionResponse = z.infer<typeof DefinitionResponseSchema>;

/* ----------------------------- Provisioning ----------------------------- */

/** Provisioning lifecycle (BFF-owned, drives the Preparing screen). */
export const ProvisionStatusSchema = z.enum([
  'pending',
  'provisioning',
  'cloning',
  'ready',
  'failed',
]);
export type ProvisionStatus = z.infer<typeof ProvisionStatusSchema>;

export const TERMINAL_PROVISION_STATUSES: ProvisionStatus[] = ['ready', 'failed'];

/** The wizard answers carried into provisioning (option ids, not labels). */
export const WizardAnswersSchema = z.object({
  objective: z.string().nullable(),
  bottleneck: z.string().nullable(),
  channels: z.array(z.string()),
  situation: z.string().nullable(),
});
export type WizardAnswers = z.infer<typeof WizardAnswersSchema>;

/**
 * Provision request. The tenant path (SUPPORT BOTH) is decided server-side from
 * the authenticated user's membership — never from the client. For the
 * register-path (new merchant) the orchestrator needs the captured credentials
 * because the user is registered TENANT-FIRST during provisioning.
 */
export const ProvisionRequestSchema = z.object({
  storeUrl: z.string().min(1),
  storeName: z.string().min(1).optional(),
  answers: WizardAnswersSchema,
  /** Present only for the register-path (new user/tenant). */
  account: z
    .object({
      mode: z.enum(['login', 'register']),
      email: z.string().email(),
      /**
       * Optional. When absent the BFF generates a strong random password for
       * the tss-svc-users account (the admin logs in via Cognito, never with
       * this). Still honoured when sent by older clients.
       */
      password: z
        .string()
        .min(1)
        .refine(isRegisterPasswordValid, REGISTER_PASSWORD_HINT)
        .optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
    })
    .optional(),
});
export type ProvisionRequest = z.infer<typeof ProvisionRequestSchema>;

export const ProvisionResponseSchema = z.object({
  provisionId: z.string(),
  tenantId: z.string(),
  storeId: z.string(),
  status: ProvisionStatusSchema,
});
export type ProvisionResponse = z.infer<typeof ProvisionResponseSchema>;

/** One row in the Preparing-screen checklist, driven by real status. */
export const ProvisionStepStatusSchema = z.object({
  key: z.string(),
  label: z.string(),
  state: z.enum(['todo', 'active', 'done', 'failed']),
});
export type ProvisionStepStatus = z.infer<typeof ProvisionStepStatusSchema>;

export const ProvisionStatusResponseSchema = z.object({
  provisionId: z.string(),
  status: ProvisionStatusSchema,
  steps: z.array(ProvisionStepStatusSchema),
  /** Set when status is `failed` (mirrors tenancy `infra_last_error`). */
  error: z.string().optional(),
  /** Present when status === 'ready' — where the FE redirects the merchant. */
  redirectUrl: z.string().url().optional(),
  /** GitHub workflow run URL surfaced by tenancy (debugging). */
  runUrl: z.string().url().optional(),
  /**
   * True while the store's infra bind has FAILED but can still be retried by an
   * operator. NON-terminal: `status` stays `provisioning` and the BFF keeps
   * waiting; the flow resumes by itself once the retry succeeds.
   */
  waitingForRetry: z.boolean().optional(),
  /** Last tenancy error behind `waitingForRetry` (operator-facing detail). */
  waitingReason: z.string().optional(),
});
export type ProvisionStatusResponse = z.infer<typeof ProvisionStatusResponseSchema>;
